import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Fab,
  Badge,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Chip,
  Stack,
  Pagination,
} from '@mui/material';
import { Compare } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

import UniversitySearch from '../components/universities/UniversitySearch';
import UniversityFilters from '../components/universities/UniversityFilters';
import UniversityCard from '../components/universities/UniversityCard';
import UniversityComparison from '../components/universities/UniversityComparison';
import { schoolService, schoolDataMapper } from '../services/schoolService';

const Universities = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 状态管理
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    type: '',
    ranking: '',
    sortBy: 'ranking',
    tuitionRange: [0, 1000000],
    programs: [],
  });
  const [allUniversities, setAllUniversities] = useState([]); // 全量数据
  const [universities, setUniversities] = useState([]); // 当前页数据
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 9
  });
  const [comparisonList, setComparisonList] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [showComparison, setShowComparison] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    types: ['公立大学', '私立大学'],
    popularPrograms: [],
    popularSearches: [],
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const universityListRef = useRef(null);

  // 从localStorage加载收藏
  useEffect(() => {
    const savedFavorites = localStorage.getItem('universityFavorites');
    if (savedFavorites) {
      setFavorites(new Set(JSON.parse(savedFavorites)));
    }
  }, []);

  // 保存收藏到localStorage
  useEffect(() => {
    localStorage.setItem('universityFavorites', JSON.stringify([...favorites]));
  }, [favorites]);

  // 加载筛选选项
  const loadFilterOptions = useCallback(async () => {
    try {
      const [degreeTypes, , stats] = [];
      // = await Promise.all([
      //   schoolService.getDegreeTypes(),
      //   schoolService.getIntakeOptions(),
      //   schoolService.getSchoolStats()
      // ]);

      setFilterOptions(prev => ({
        ...prev,
        popularPrograms: Array.isArray(degreeTypes?.data || degreeTypes) ? (degreeTypes?.data || degreeTypes) : ['理科','会计', '计算机', '金融', '管理', '法律', '教育', '艺术', '医学', '工程', '商科', '计算机科学', '法学'],
        popularSearches: Array.isArray(degreeTypes?.data || degreeTypes) ? (degreeTypes?.data || degreeTypes) : ['马来亚', '国民', '博特拉', '诺丁汉', '泰莱', '理科', '思特雅', '理工', 'USM', 'UTM', 'UPM', 'UKM', 'Taylor'],
        locations: (stats?.data?.states && Array.isArray(stats.data.states)) 
          ? stats.data.states 
          : ['吉隆坡', '雪兰莪', '槟城', '柔佛', '沙巴']
      }));
    } catch (error) {
      console.error('加载筛选选项失败:', error);
      // 设置默认值，确保组件正常工作
      setFilterOptions(prev => ({
        ...prev,
        popularPrograms: ['医学', '工程学', '商科', '计算机科学', '法学', '教育'],
        popularSearches: ['医学', '工程学', '商科', '计算机科学', '法学', '教育'],
        locations: ['吉隆坡', '雪兰莪', '槟城', '柔佛', '沙巴']
      }));
      // setNotification({
      //   open: true,
      //   message: '加载筛选选项失败，使用默认选项',
      //   severity: 'warning'
      // });
    }
  }, []);

  // 加载学校全量数据（只请求一次）
  const loadAllUniversities = useCallback(async () => {
    setLoading(true);
    try {
      // 不传分页参数，获取全部数据
      const apiParams = schoolDataMapper.mapFiltersToApiParams({}, ''); // 不带筛选
      const response = await schoolService.getSchools({ ...apiParams });
      const schoolsData = response?.data || response.content || [];
      const mappedUniversities = schoolsData.map(school => 
        schoolDataMapper.mapSchoolFromApi(school)
      );
      setAllUniversities(mappedUniversities);
    } catch (error) {
      console.error('加载学校数据失败:', error);
      setNotification({
        open: true,
        message: '加载学校数据失败，请稍后重试',
        severity: 'error'
      });
      setAllUniversities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 处理URL参数
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    const locationFromUrl = searchParams.get('location');
    const typeFromUrl = searchParams.get('type');
    
    // 只有当URL参数存在且与当前状态不同时才更新
    if (searchFromUrl !== null) {
      setSearchTerm(searchFromUrl || '');
    }
    if (locationFromUrl !== null) {
      setFilters(prev => ({ ...prev, location: locationFromUrl || '' }));
    }
    if (typeFromUrl !== null) {
      setFilters(prev => ({ ...prev, type: typeFromUrl || '' }));
    }
  }, [searchParams]);

  // 初始化加载（只加载一次）
  useEffect(() => {
    loadFilterOptions();
    loadAllUniversities();
  }, [loadFilterOptions, loadAllUniversities]);

  // 前端分页、搜索、排序、筛选逻辑
  useEffect(() => {
    let filtered = allUniversities;
    // 搜索
    if (searchTerm) {
      
      filtered = filtered.filter(u =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.Abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    // 筛选
    if (filters.location) {
      filtered = filtered.filter(u => 
        u.location && (
          (u.location.state && u.location.state.includes(filters.location)) ||
          (u.location.city && u.location.city.includes(filters.location))
        )
      );
    }
    if (filters.type) {
      filtered = filtered.filter(u => u.type === filters.type);
    }
    if (filters.ranking) {
      filtered = filtered.filter(u => {
        if (!u.ranking) return false;
        
        const filterRange = Number(filters.ranking); // 过滤器表示前N名，如50表示前50名
        const rankingStr = String(u.ranking).trim();
        
        // 处理带年份的格式，如 "711（2024）" -> 提取数字部分
        const cleanRanking = rankingStr.replace(/（\d{4}）/g, '').trim();
        
        // 如果是区间格式，如 "100-200"
        if (cleanRanking.includes('-')) {
          const [min, max] = cleanRanking.split('-').map(Number);
          // 判断区间是否与"前N名"范围有交集
          return min <= filterRange;
        }
        
        // 如果是纯数字
        const rankingNum = Number(cleanRanking);
        if (!isNaN(rankingNum)) {
          // 判断排名是否在前N名范围内
          return rankingNum <= filterRange;
        }
        
        return false;
      });
    }
    if (filters.programs && filters.programs.length > 0) {
      filtered = filtered.filter(u =>
        u.programs && filters.programs.some(p => u.programs.includes(p))
      );
    }
    // 学费区间筛选（区间有交集）
    if (filters.tuitionRange) {
      filtered = filtered.filter(u => {
        const min = Number(u.FeesMin);
        const max = Number(u.FeesMax);
        return (
          !isNaN(min) && !isNaN(max) &&
          max >= filters.tuitionRange[0] && min <= filters.tuitionRange[1]
        );
      });
    }
    // 排序
    if (filters.sortBy === 'ranking') {
      filtered = [...filtered].sort((a, b) => {
        if (a.rankingValue == null && b.rankingValue == null) return 0;
        if (a.rankingValue == null) return 1;
        if (b.rankingValue == null) return -1;
        return a.rankingValue - b.rankingValue;
      });
    } else if (filters.sortBy === 'tuition-low') {
      filtered = [...filtered].sort((a, b) => a.tuitionFee.international - b.tuitionFee.international);
    } else if (filters.sortBy === 'tuition-high') {
      filtered = [...filtered].sort((a, b) => b.tuitionFee.international - a.tuitionFee.international);
    } else if (filters.sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    // 分页
    const totalElements = filtered.length;
    const totalPages = Math.ceil(totalElements / pagination.size);
    const currentPage = Math.min(pagination.currentPage, totalPages - 1 >= 0 ? totalPages - 1 : 0);
    const startIdx = currentPage * pagination.size;
    const endIdx = startIdx + pagination.size;
    setUniversities(filtered.slice(startIdx, endIdx));
    setPagination(prev => ({
      ...prev,
      currentPage,
      totalPages,
      totalElements
    }));
  }, [allUniversities, filters, searchTerm, pagination.currentPage, pagination.size]);

  // 处理分页（只切换页码，不请求后端）
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage - 1 }));
    if (universityListRef.current) {
      universityListRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 处理搜索
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, currentPage: 0 })); // 搜索重置到第一页
  };

  // 处理筛选
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 0 })); // 筛选重置到第一页
  };

  // 清除筛选
  const handleClearFilters = () => {
    setFilters({
      location: '',
      type: '',
      ranking: '',
      sortBy: 'ranking',
      tuitionRange: [0, 1000000],
      programs: [],
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, currentPage: 0 }));
    // 清除URL参数
    setSearchParams({});
  };

  // 添加到对比
  const handleAddToComparison = (university) => {
    if (comparisonList.length >= 3) {
      setNotification({
        open: true,
        message: '最多只能对比3所大学',
        severity: 'warning'
      });
      return;
    }

    if (comparisonList.find(u => u.id === university.id)) {
      setNotification({
        open: true,
        message: '该大学已在对比列表中',
        severity: 'info'
      });
      return;
    }

    setComparisonList(prev => [...prev, university]);
    setNotification({
      open: true,
      message: `已添加 ${university.name} 到对比列表`,
      severity: 'success'
    });
  };

  // 从对比中移除
  const handleRemoveFromComparison = (universityId) => {
    setComparisonList(prev => prev.filter(u => u.id !== universityId));
  };

  // 切换收藏
  const handleToggleFavorite = (universityId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(universityId)) {
        newFavorites.delete(universityId);
        setNotification({
          open: true,
          message: '已从收藏中移除',
          severity: 'info'
        });
      } else {
        newFavorites.add(universityId);
        setNotification({
          open: true,
          message: '已添加到收藏',
          severity: 'success'
        });
      }
      return newFavorites;
    });
  };

  // 计算活跃筛选数量
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.location) count++;
    if (filters.type) count++;
    if (filters.ranking) count++;
    if (filters.programs && filters.programs.length > 0) count++;
    // 检查学费范围是否与默认值不同（默认值是 [0, 1000000]）
    if (filters.tuitionRange && 
        (filters.tuitionRange[0] !== 0 || filters.tuitionRange[1] !== 1000000)) count++;
    return count;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* 页面标题 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            马来西亚大学库
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            发现适合您的理想大学
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Chip label={`共${allUniversities.length}所大学`} color="primary" />
            <Chip label="实时数据" color="success" />
            {/* <Chip label="官方认证" color="info" /> */}
          </Stack>
        </Box>
      </motion.div>

      {/* 搜索组件 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <UniversitySearch
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          resultCount={pagination.totalElements}
          popularSearches={Array.isArray(filterOptions.popularSearches) ? filterOptions.popularSearches : []}
        />
      </motion.div>

      {/* 筛选组件 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <UniversityFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          filterOptions={filterOptions}
          activeFiltersCount={getActiveFiltersCount()}
        />
      </motion.div>

      {/* 加载状态 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {/* 大学列表 */}
      {!loading && (
        <motion.div
          ref={universityListRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {universities.length > 0 ? (
            <>
              <Grid container spacing={3}>
                <AnimatePresence>
                  {universities.map((university, index) => (
                    <Grid item xs={12} sm={6} lg={4} key={university.id} sx={{ display: 'flex' }}>
                      <motion.div
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <UniversityCard
                          university={university}
                          isFavorite={favorites.has(university.id)}
                          onToggleFavorite={handleToggleFavorite}
                          onAddToComparison={handleAddToComparison}
                          isInComparison={comparisonList.some(u => u.id === university.id)}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>

              {/* 分页组件 */}
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Paper sx={{ p: 2 }}>
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.currentPage + 1} // Material-UI分页从1开始，useEffect中的currentPage是0-based
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                  </Paper>
                </Box>
              )}
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                没有找到符合条件的大学
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                试试调整筛选条件或搜索关键词
              </Typography>
              <Button variant="outlined" onClick={handleClearFilters}>
                清除所有筛选
              </Button>
            </Paper>
          )}
        </motion.div>
      )}

      {/* 对比浮动按钮 */}
      {comparisonList.length > 0 && (
        <Fab
          color="secondary"
          aria-label="compare"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 80, // 向左移动，避免与回到顶部按钮重合
            zIndex: 1000,
          }}
          onClick={() => setShowComparison(true)}
        >
          <Badge badgeContent={comparisonList.length} color="error">
            <Compare />
          </Badge>
        </Fab>
      )}

      {/* 对比弹窗 */}
      <UniversityComparison
        open={showComparison}
        onClose={() => setShowComparison(false)}
        universities={comparisonList}
        onRemoveUniversity={handleRemoveFromComparison}
      />

      {/* 通知提示 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Universities;
